/* Reservation card product page (sections/ls-rc-product.liquid).
 * Card: pointer tilt + foil follow (fine pointers), slow float + sheen sweep while on screen; GSAP via window.lsLoadGsap; none under reduced motion.
 * Buy: AJAX add to cart, then Minimog's cart drawer (falls back to /cart). Checkbox agreement mode keeps the button disabled until ticked. */
(() => {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const fine = window.matchMedia('(hover: hover) and (pointer: fine)');

  const initCard = (root) => {
    const card = root.querySelector('[data-lrc-tilt]');
    if (!card || reduce.matches || !window.lsLoadGsap) return;
    const stage = root.matches('[data-lrc-stage]') ? root : (root.querySelector('.lrc__stage, [data-lrc-stage]') || card.parentElement);
    const k = parseFloat(root.dataset.lrcTilt) || 1;
    window.lsLoadGsap(root.dataset.gsap).then((gsap) => {
      const angle = { x: 0, y: 0 };
      const apply = () => {
        card.style.setProperty('--rx', `${angle.x.toFixed(2)}deg`);
        card.style.setProperty('--ry', `${angle.y.toFixed(2)}deg`);
      };
      const rx = gsap.quickTo(angle, 'x', { duration: 0.6, ease: 'power3.out', onUpdate: apply });
      const ry = gsap.quickTo(angle, 'y', { duration: 0.6, ease: 'power3.out', onUpdate: apply });
      if (fine.matches) {
        stage.addEventListener('pointermove', (e) => {
          const r = card.getBoundingClientRect();
          const x = Math.max(-0.75, Math.min(0.75, (e.clientX - r.left) / r.width - 0.5));
          const y = Math.max(-0.75, Math.min(0.75, (e.clientY - r.top) / r.height - 0.5));
          rx(-y * 10 * k);
          ry(x * 12 * k);
          gsap.to(card, { '--mx': `${Math.round((x + 0.5) * 100)}%`, '--my': `${Math.round((y + 0.5) * 100)}%`, '--sheen': 0.7, duration: 0.5, ease: 'power2.out', overwrite: 'auto' });
        });
        if (root.dataset.lrcLift) {
          stage.addEventListener('pointerenter', () => gsap.to(card, { '--s': parseFloat(root.dataset.lrcLift) || 1.03, duration: 0.5, ease: 'power3.out', overwrite: 'auto' }));
          stage.addEventListener('pointerleave', () => gsap.to(card, { '--s': 1, duration: 0.6, ease: 'power3.out' }));
        }
        stage.addEventListener('pointerleave', () => {
          rx(0);
          ry(0);
          gsap.to(card, { '--sheen': 0.35, duration: 0.6, ease: 'power2.out', overwrite: 'auto' });
        });
      }
      let loops = null;
      new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting && !loops) {
          const float = gsap.timeline({ repeat: -1, defaults: { ease: 'sine.inOut' } })
            .to(card, { '--ty': '-10px', duration: 3.2 })
            .to(card, { '--ty': '0px', duration: 3.2 });
          const sweep = gsap.fromTo(card, { '--mx': '-30%' }, { '--mx': '130%', duration: 2.4, ease: 'power2.inOut', repeat: -1, repeatDelay: 4.5, delay: 0.8 });
          loops = [float, sweep];
        } else if (!entry.isIntersecting && loops) {
          loops.forEach((t) => t.kill());
          loops = null;
        }
      }, { threshold: 0.1 }).observe(stage);
    }).catch(() => {});
  };

  const initBuy = (root) => {
    const form = root.querySelector('[data-lrc-form]');
    if (!form) return;
    const btn = form.querySelector('[data-lrc-submit]');
    const label = form.querySelector('[data-lrc-label]');
    const error = form.querySelector('[data-lrc-error]');
    const agree = form.querySelector('[data-lrc-agree]');
    const available = btn && !btn.disabled;
    let busy = false;

    const sync = () => {
      if (!btn || !available) return;
      btn.disabled = busy || (agree ? !agree.checked : false);
      btn.setAttribute('aria-busy', busy ? 'true' : 'false');
      if (label) label.textContent = busy ? (root.dataset.adding || btn.dataset.label) : btn.dataset.label;
    };
    const showError = (msg) => {
      if (!error) return;
      error.textContent = msg || '';
      error.hidden = !msg;
    };
    agree?.addEventListener('change', sync);
    sync();

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (busy || (agree && !agree.checked)) return;
      busy = true;
      showError('');
      sync();
      const drawer = document.querySelector('m-cart-drawer');
      const useDrawer = drawer && window.MinimogSettings?.enable_cart_drawer && typeof drawer.renderContents === 'function';
      const body = new FormData(form);
      if (useDrawer) {
        body.append('sections', drawer.getSectionsToRender().map((s) => s.id));
        body.append('sections_url', window.location.pathname);
      }
      try {
        const response = await fetch((window.Shopify?.routes?.root || '/') + 'cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body,
        });
        const result = await response.json();
        if (!response.ok || result.status) {
          showError(result.description || result.message || root.dataset.error);
          return;
        }
        document.dispatchEvent(new CustomEvent('product-ajax:added', { detail: { product: result } }));
        if (useDrawer) {
          drawer.renderContents(result);
          drawer.getCart?.().then((cart) => cart && drawer.updateCartCount(cart.item_count));
        } else {
          window.location.href = (window.Shopify?.routes?.root || '/') + 'cart';
        }
      } catch (err) {
        console.warn(err);
        showError(root.dataset.error);
      } finally {
        busy = false;
        sync();
      }
    });
  };

  const initDrawers = (root) => {
    root.querySelectorAll('[data-lrc-drawer]').forEach((drawer) => {
      let trigger = null;
      drawer.querySelectorAll('[data-lrc-drawer-close]').forEach((b) => b.addEventListener('click', () => drawer.close()));
      drawer.addEventListener('click', (e) => { if (e.target === drawer) drawer.close(); });
      drawer.addEventListener('close', () => {
        document.documentElement.classList.remove('ls-scroll-lock');
        if (trigger && document.contains(trigger)) trigger.focus({ preventScroll: true });
      });
      root.querySelectorAll(`[data-lrc-drawer-open="${drawer.id}"]`).forEach((btn) => btn.addEventListener('click', () => {
        if (typeof drawer.showModal !== 'function' || drawer.open) return;
        trigger = btn;
        drawer.querySelector('.lrc-drawer__scroll')?.scrollTo(0, 0);
        drawer.showModal();
        document.documentElement.classList.add('ls-scroll-lock');
        drawer.querySelector('[data-lrc-drawer-close]')?.focus({ preventScroll: true });
      }));
    });
  };

  const init = () => document.querySelectorAll('[data-lrc]').forEach((root) => {
    if (root.dataset.lrcReady) return;
    root.dataset.lrcReady = '1';
    initCard(root);
    initBuy(root);
    initDrawers(root);
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();
